import loadable from '@loadable/component';
import { Button, Grid, Text, useTheme, Heading } from '@simplywallst/ui-core';

import type { UnmatchedHolding } from '@/hooks/portfolio/usePortfolio';

import { useMatchingMachine } from './hooks/useMatchingMachine';
import { useRemoveMachine } from './hooks/useRemoveMachine';

import { TableRowCell, TableRow } from '../Table';
import ActionSheet, {
  Background,
  Header,
  Sheet,
} from '@/components/ActionSheet';
import { CompanySearch } from './components/CompanySearch';

const MatchingConfirmationModal = loadable(
  () => import('./components/MatchingConfirmationModal')
);
const RemoveConfirmationModal = loadable(
  () => import('../RemoveLinkedPortfolioHoldingModal'),
  {
    resolveComponent: (c) => c.RemoveUnmatchedHoldingModal,
  }
);

interface Props {
  unmatchedHolding: UnmatchedHolding;
  'data-cy-id'?: string;
}

const UnmatchedHoldingRowDesktop = ({
  unmatchedHolding,
  'data-cy-id': dataCyId,
}: Props) => {
  const theme = useTheme();
  const [removeState, removeSend] = useRemoveMachine();
  const [matchState, matchSend] = useMatchingMachine();
  const isSearchOpen = matchState.current === 'search';

  return (
    <>
      <TableRow data-cy-id={dataCyId}>
        <TableRowCell data-cy-id="unmatchedHolding-column">
          <Grid alignContent="center">
            <Text typography="xSmall">{unmatchedHolding.symbol}</Text>
            <Text typography="tiny" color="whiteRGBA" opacity={0.5} isTruncated>
              {unmatchedHolding.providerCompanyName ?? 'Unknown'}
            </Text>
          </Grid>
        </TableRowCell>
        <TableRowCell colSpan={10}>
          <Grid alignItems="center" templateColumns="auto max-content">
            <Text typography="tiny" color="whiteRGBA" opacity={0.5}>
              Symbol can&apos;t be found
            </Text>
            {matchState.current === 'idle' ? (
              <Grid
                templateColumns="max-content auto"
                alignItems="center"
                gap="16px"
              >
                <Button
                  styleType="loud"
                  onClick={() => matchSend({ current: 'search' })}
                >
                  Match Ticker
                </Button>
                <Button
                  styleType="passive"
                  onClick={() => removeSend({ current: 'confirm' })}
                >
                  Remove
                </Button>
              </Grid>
            ) : null}
            {isSearchOpen ? (
              <ActionSheet open onClose={() => matchSend({ current: 'idle' })}>
                <Background />
                <Sheet>
                  <Grid templateRows={`calc(50px + ${theme.safeAreaInsetTop})`}>
                    <Grid
                      row="2"
                      borderTopLeftRadius="x2"
                      borderTopRightRadius="x2"
                      backgroundColor="noir"
                      templateRows="max-content auto"
                    >
                      <Header>
                        <Heading as="h3">Match Ticker</Heading>
                      </Header>
                      <CompanySearch
                        onSelectHolding={(uniqueSymbol: string) => {
                          matchSend({
                            current: 'confirm',
                            companyUniqueSymbol: uniqueSymbol,
                          });
                        }}
                      />
                    </Grid>
                  </Grid>
                </Sheet>
              </ActionSheet>
            ) : null}
          </Grid>
        </TableRowCell>
      </TableRow>
      {matchState.current === 'confirm' ? (
        <MatchingConfirmationModal
          unmatchedHolding={unmatchedHolding}
          companyUniqueSymbol={matchState.companyUniqueSymbol}
          onClose={() => matchSend({ current: 'search' })}
          onSuccess={() => matchSend({ current: 'success' })}
          onError={() => matchSend({ current: 'idle' })}
        />
      ) : null}
      {removeState.current === 'confirm' ? (
        <RemoveConfirmationModal
          unmatchedHolding={unmatchedHolding}
          onError={() => removeSend({ current: 'idle' })}
          onCancel={() => removeSend({ current: 'idle' })}
          onSuccess={() => removeSend({ current: 'success' })}
        />
      ) : null}
    </>
  );
};

export default UnmatchedHoldingRowDesktop;
